import React from 'react';
import {
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  ALL_OPTIMIZATION_STATUSES,
  type OptimizationFilterOptions,
  type OptimizationFilterState,
  type OptimizationStatusValue,
} from '@/screens/optimization/types';

interface OptimizationFiltersProps {
  filters: OptimizationFilterState;
  onFiltersChange: (filters: OptimizationFilterState) => void;
  data: OptimizationFilterOptions;
  loading?: boolean;
  error?: string | null;
}

// Design filter-bar chip look (same treatment as the Reporting filter bar):
// compact, rounded, light-fill fields. Styling only — the controls keep the
// old UI's value/handler/option shapes so filtering applies identically.
const fieldSx = {
  flex: '1 1 0',
  minWidth: 0,
  '& .MuiOutlinedInput-root': {
    borderRadius: '9px',
    background: '#F9FBFE',
    fontSize: 13,
    fontWeight: 700,
  },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E1E7F0' },
  '& .MuiInputLabel-root': {
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: '.3px',
    color: '#9BA6B7',
  },
} as const;

const labelSx = {
  fontSize: 12.5,
  fontWeight: 700,
  color: '#7A8497',
  mr: 0.5,
  flex: 'none',
} as const;

function OptimizationFilters({
  filters,
  onFiltersChange,
  data,
  loading = false,
  error = null,
}: OptimizationFiltersProps) {
  const update = (partial: Partial<OptimizationFilterState>) => {
    onFiltersChange({ ...filters, ...partial });
  };

  const handleSelectChange =
    (field: keyof OptimizationFilterState) =>
    (event: SelectChangeEvent) => {
      update({ [field]: event.target.value });
    };

  const selectedStatuses =
    filters.statuses === 'all' ? ALL_OPTIMIZATION_STATUSES : filters.statuses;

  const handleStatusToggle = (
    _event: React.MouseEvent<HTMLElement>,
    newStatuses: OptimizationStatusValue[],
  ) => {
    if (newStatuses.length === 0) {
      return;
    }
    if (newStatuses.length === ALL_OPTIMIZATION_STATUSES.length) {
      update({ statuses: 'all' });
    } else {
      update({ statuses: newStatuses });
    }
  };

  const renderOptions = (
    options: { value: string; label: string }[],
  ) => [
    <MenuItem key="__all" value="">
      All
    </MenuItem>,
    loading ? (
      <MenuItem key="__loading" disabled>
        <CircularProgress size={20} />
      </MenuItem>
    ) : (
      options.map((option) => (
        <MenuItem key={option.value} value={option.value}>
          {option.label}
        </MenuItem>
      ))
    ),
  ];

  return (
    <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: { xs: 'wrap', md: 'nowrap' },
          gap: 1,
        }}>
        <Box component="span" sx={labelSx}>
          Filters
        </Box>

        <FormControl size="small" sx={fieldSx}>
          <InputLabel id="opt-department-label">Department</InputLabel>
          <Select
            labelId="opt-department-label"
            value={filters.departmentId}
            label="Department"
            onChange={handleSelectChange('departmentId')}
            disabled={loading}>
            {renderOptions(
              data.departments.map((department) => ({
                value: department.id.toString(),
                label: department.name,
              })),
            )}
          </Select>
        </FormControl>

        <FormControl size="small" sx={fieldSx}>
          <InputLabel id="opt-cloud-account-label">Cloud Account</InputLabel>
          <Select
            labelId="opt-cloud-account-label"
            value={filters.cloudAccountId}
            label="Cloud Account"
            onChange={handleSelectChange('cloudAccountId')}
            disabled={loading}>
            {renderOptions(
              data.cloudAccounts.map((cloudAccount) => ({
                value: cloudAccount.id.toString(),
                label: cloudAccount.name,
              })),
            )}
          </Select>
        </FormControl>

        <FormControl size="small" sx={fieldSx}>
          <InputLabel id="opt-subscription-label">Subscription</InputLabel>
          <Select
            labelId="opt-subscription-label"
            value={filters.subscriptionId}
            label="Subscription"
            onChange={handleSelectChange('subscriptionId')}
            disabled={loading}>
            {renderOptions(
              data.subscriptions.map((subscription) => ({
                value: subscription.subscription_id,
                label: subscription.display_name,
              })),
            )}
          </Select>
        </FormControl>

        <FormControl size="small" sx={fieldSx}>
          <InputLabel id="opt-system-label">System</InputLabel>
          <Select
            labelId="opt-system-label"
            value={filters.systemId}
            label="System"
            onChange={handleSelectChange('systemId')}
            disabled={loading}>
            {renderOptions(
              data.systems.map((system) => ({
                value: system.id.toString(),
                label: system.name,
              })),
            )}
          </Select>
        </FormControl>

        <FormControl size="small" sx={fieldSx}>
          <InputLabel id="opt-owner-label">Resource owner</InputLabel>
          <Select
            labelId="opt-owner-label"
            value={filters.resourceOwnerId}
            label="Resource owner"
            onChange={handleSelectChange('resourceOwnerId')}
            disabled={loading}>
            {renderOptions(
              data.users.map((user) => ({
                value: user.id.toString(),
                label: `${user.firstName} (${user.username})`,
              })),
            )}
          </Select>
        </FormControl>
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
        <Box component="span" sx={labelSx}>
          Status
        </Box>
        <ToggleButtonGroup
          value={selectedStatuses}
          exclusive={false}
          onChange={handleStatusToggle}
          size="small"
          color="primary">
          {ALL_OPTIMIZATION_STATUSES.map((status) => (
            <ToggleButton key={status} value={status}>
              {status}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      {error && (
        <Box sx={{ color: 'error.main', fontSize: '0.875rem' }}>
          Error loading filter data: {error}
        </Box>
      )}
    </Box>
  );
}

export default OptimizationFilters;
